import mapboxgl from "mapbox-gl"
import type { LngLatBoundsLike } from "mapbox-gl"
import { MAP_STYLES, type MapStyleValue } from "@/components/constants"
import { BASE_MAP_STYLES } from "@/components/map/baseMapStyles"
import { addParcels, PARCEL_SOURCE_LAYER, ParcelsTheme } from "@/components/map/parcelsLayer"
import {
	addEndangeredSpecies,
	addFloodableZones,
	addWetlands,
	endangeredSpeciesLayerDefinitions,
} from "@/components/map/environmentLayers"
import { addPlanningPolicy, addZoning, ZONING_SOURCE_LAYER, ZONING_SOURCE_NAME } from "@/components/map/planningLayers"
import { loadHatchPatterns } from "@/components/map/layerPatterns"
import { tileRequestHeaders } from "@/utils/tileRequestHeaders"
import { parcelSides } from "@/utils/parcelDimensions"

const MAP_WIDTH = 1100
const MAP_HEIGHT = 500
const WIDE_MAP_HEIGHT = 1050
const COVER_MAP_HEIGHT = 444
const ZONING_MAP_HEIGHT = 380
const SPECIES_MAP_HEIGHT = 351
const DEFAULT_MAP_ZOOM = 15
const MIN_MAP_ZOOM = 13
const MIN_ZONING_MAP_ZOOM = 14
const MIN_WIDE_MAP_ZOOM = 11
const MAX_MAP_ZOOM = 16
const MAX_CLOSE_UP_ZOOM = 19
const CONTEXT_ZOOM_OUT = 1
const WIDE_VIEW_BASE_WIDTH_M = 1000
const WIDE_VIEW_PARCEL_FACTOR = 5
const METERS_PER_DEGREE_OF_LATITUDE = 111320
const CLOSE_UP_VIEW_WIDTH_M = 200
const SATELLITE_NEIGHBOUR_PARCEL_LINE_OPACITY = 0.6
const FOOTPRINT_ATTEMPTS = 3
const MAP_PADDING = 80
const CAPTURE_TIMEOUT_MS = 20000
const EARTH_CIRCUMFERENCE_M = 40075016.686

export const PARCEL_OUTLINE_COLOR = "#0d6349"
export const SATELLITE_PARCEL_OUTLINE_COLOR = "#2ff070"

export enum ParcelMapView {
	Location = "location",
	CoverLocation = "cover-location",
	CloseUp = "close-up",
	Zoning = "zoning",
	PlanningPolicy = "planning-policy",
	Wetlands = "wetlands",
	FloodableZones = "floodable-zones",
	EndangeredSpecies = "endangered-species",
}

interface ParcelMapViewSpec {
	layers: string[]
	baseMap: MapStyleValue
	height: number
	zoom: (fittedZoom: number | undefined, latitude: number, parcelSpanInMeters: number) => number
}

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)

function zoomForWidth(widthInMeters: number, latitude: number): number {
	return Math.log2((EARTH_CIRCUMFERENCE_M * Math.cos((latitude * Math.PI) / 180) * MAP_WIDTH) / (512 * widthInMeters))
}

const surroundingsZoom = (fittedZoom: number | undefined) =>
	fittedZoom === undefined ? DEFAULT_MAP_ZOOM : clamp(fittedZoom - CONTEXT_ZOOM_OUT, MIN_MAP_ZOOM, MAX_MAP_ZOOM)

const HATCHED_VIEWS = [ParcelMapView.PlanningPolicy, ParcelMapView.EndangeredSpecies]

const viewSpecs: Record<ParcelMapView, ParcelMapViewSpec> = {
	[ParcelMapView.Location]: {
		layers: [],
		baseMap: MAP_STYLES.SATELLITE,
		height: WIDE_MAP_HEIGHT,
		zoom: (_, latitude, parcelSpanInMeters) =>
			clamp(
				zoomForWidth(WIDE_VIEW_BASE_WIDTH_M + WIDE_VIEW_PARCEL_FACTOR * parcelSpanInMeters, latitude),
				MIN_WIDE_MAP_ZOOM,
				MAX_MAP_ZOOM
			),
	},
	[ParcelMapView.CoverLocation]: {
		layers: [],
		baseMap: MAP_STYLES.SATELLITE,
		height: COVER_MAP_HEIGHT,
		zoom: (_, latitude, parcelSpanInMeters) =>
			clamp(
				zoomForWidth(WIDE_VIEW_BASE_WIDTH_M + WIDE_VIEW_PARCEL_FACTOR * parcelSpanInMeters, latitude),
				MIN_WIDE_MAP_ZOOM,
				MAX_MAP_ZOOM
			),
	},
	[ParcelMapView.CloseUp]: {
		layers: ["parcel-report-dimensions"],
		baseMap: MAP_STYLES.SATELLITE,
		height: MAP_HEIGHT,
		zoom: (fittedZoom, latitude) => {
			const zoom = zoomForWidth(CLOSE_UP_VIEW_WIDTH_M, latitude)
			return clamp(Math.min(zoom, fittedZoom ?? zoom), MIN_MAP_ZOOM, MAX_CLOSE_UP_ZOOM)
		},
	},
	[ParcelMapView.Zoning]: {
		layers: ["zoning", "zoning-outline", "zoning-labels"],
		baseMap: MAP_STYLES.SIMPLE,
		height: ZONING_MAP_HEIGHT,
		zoom: fittedZoom =>
			fittedZoom === undefined
				? DEFAULT_MAP_ZOOM
				: clamp(fittedZoom - CONTEXT_ZOOM_OUT, MIN_ZONING_MAP_ZOOM, MAX_MAP_ZOOM),
	},
	[ParcelMapView.PlanningPolicy]: {
		layers: ["planning-policy", "planning-policy-hatch", "planning-policy-line"],
		baseMap: MAP_STYLES.SIMPLE,
		height: MAP_HEIGHT,
		zoom: surroundingsZoom,
	},
	[ParcelMapView.EndangeredSpecies]: {
		layers: [
			...endangeredSpeciesLayerDefinitions.flatMap(({ id }) => [id, `${id}-plants`, `${id}-line`]),
			"endangered-species-labels",
		],
		baseMap: MAP_STYLES.SIMPLE,
		height: SPECIES_MAP_HEIGHT,
		zoom: surroundingsZoom,
	},
	[ParcelMapView.Wetlands]: {
		layers: ["wetlands"],
		baseMap: MAP_STYLES.SIMPLE,
		height: MAP_HEIGHT,
		zoom: surroundingsZoom,
	},
	[ParcelMapView.FloodableZones]: {
		layers: ["floodable-zones"],
		baseMap: MAP_STYLES.SIMPLE,
		height: MAP_HEIGHT,
		zoom: surroundingsZoom,
	},
}

interface ParcelMapSnapshotOptions {
	parcelId: string
	latitude: number
	longitude: number
	apiToken: string | null | undefined
	outline: [number, number][]
	formatLength: (meters: number) => string
	zoneCode?: string
	locale: string
	onViewCaptured?: () => void
}

interface ParcelMapCapture {
	view: ParcelMapView
	image: string | null
	widthInMeters: number
}

function addViewLayers(
	map: mapboxgl.Map,
	view: ParcelMapView,
	baseUrl: string,
	{ zoneCode, locale }: Pick<ParcelMapSnapshotOptions, "zoneCode" | "locale">
): void {
	const visibleLayers: string[] = []
	switch (view) {
		case ParcelMapView.PlanningPolicy:
			addPlanningPolicy(map, {
				baseUrl: import.meta.env.VITE_TILES_URL_LIVE,
				visibleLayers,
				sourceName: "planning-policy-source",
				layerName: "planning-policy",
			})
			break
		case ParcelMapView.EndangeredSpecies:
			addEndangeredSpecies(map, {
				baseUrl,
				visibleLayers,
				sourceName: "endangered-species-source",
				enabledSpecies: { threatenedOrVulnerable: true, susceptible: true, candidate: true, masked: true },
				locale,
			})
			break
		case ParcelMapView.Zoning:
			addZoning(map, { baseUrl: import.meta.env.VITE_TILES_URL_LIVE, visibleLayers, isSatellite: false })
			if (zoneCode) {
				map.setFeatureState(
					{ source: ZONING_SOURCE_NAME, sourceLayer: ZONING_SOURCE_LAYER, id: zoneCode },
					{ selected: true }
				)
			}
			break
		case ParcelMapView.Wetlands:
			addWetlands(map, {
				baseUrl,
				visibleLayers,
				sourceName: "wetlands-source",
				layerName: "wetlands",
			})
			break
		case ParcelMapView.FloodableZones:
			addFloodableZones(map, {
				baseUrl,
				visibleLayers,
				sourceName: "floodable-zones-source",
				layerName: "floodable-zones",
			})
			break
	}
}

function addParcelDimensions(
	map: mapboxgl.Map,
	outline: [number, number][],
	formatLength: (meters: number) => string
): void {
	map.addSource("parcel-report-dimensions", {
		type: "geojson",
		data: {
			type: "FeatureCollection",
			features: parcelSides(outline).map(side => ({
				type: "Feature",
				geometry: { type: "Point", coordinates: side.midpoint },
				properties: { label: formatLength(side.lengthInMeters), angle: side.angle, priority: -side.lengthInMeters },
			})),
		},
	})
	map.addLayer({
		id: "parcel-report-dimensions",
		type: "symbol",
		source: "parcel-report-dimensions",
		layout: {
			visibility: "none",
			"text-field": ["get", "label"],
			"text-rotate": ["get", "angle"],
			"text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
			"text-size": 13,
			"text-allow-overlap": false,
			"symbol-sort-key": ["get", "priority"],
		},
		paint: {
			"text-color": "#1d2622",
			"text-halo-color": "#ffffff",
			"text-halo-width": 2,
		},
	})
}

function addParcelHighlight(map: mapboxgl.Map, parcelId: string, isSatellite: boolean): void {
	const parcelLayer = { source: "cadastre-source", "source-layer": PARCEL_SOURCE_LAYER, filter: parcelFilter(parcelId) }
	const color = isSatellite ? SATELLITE_PARCEL_OUTLINE_COLOR : PARCEL_OUTLINE_COLOR
	map.addLayer({
		id: "parcel-report-fill",
		type: "fill",
		...parcelLayer,
		paint: { "fill-color": color, "fill-opacity": 0.12 },
	})
	if (!isSatellite) {
		map.addLayer({
			id: "parcel-report-halo",
			type: "line",
			...parcelLayer,
			layout: { "line-join": "round" },
			paint: { "line-color": "#ffffff", "line-width": 7 },
		})
	}
	map.addLayer({
		id: "parcel-report-outline",
		type: "line",
		...parcelLayer,
		layout: { "line-join": "round" },
		paint: { "line-color": color, "line-width": 3.5 },
	})
}

function parcelFilter(parcelId: string): mapboxgl.FilterSpecification {
	return ["==", ["to-string", ["get", "parcel_id"]], parcelId]
}

function parcelFootprint(map: mapboxgl.Map, parcelId: string): [number, number][] {
	const features = map.querySourceFeatures("cadastre-source", {
		sourceLayer: PARCEL_SOURCE_LAYER,
		filter: parcelFilter(parcelId),
	})
	return features.flatMap(feature => {
		if (feature.geometry.type === "Polygon") return feature.geometry.coordinates.flat()
		if (feature.geometry.type === "MultiPolygon") return feature.geometry.coordinates.flat(2)
		return []
	}) as [number, number][]
}

function boundsAround(center: [number, number], footprint: [number, number][]): LngLatBoundsLike {
	const halfWidth = Math.max(...footprint.map(([longitude]) => Math.abs(longitude - center[0])))
	const halfHeight = Math.max(...footprint.map(([, latitude]) => Math.abs(latitude - center[1])))
	return [
		[center[0] - halfWidth, center[1] - halfHeight],
		[center[0] + halfWidth, center[1] + halfHeight],
	]
}

function parcelSpanInMeters(footprint: [number, number][]): number {
	if (!footprint.length) return 0
	const longitudes = footprint.map(([longitude]) => longitude)
	const latitudes = footprint.map(([, latitude]) => latitude)
	const latitude = (((Math.min(...latitudes) + Math.max(...latitudes)) / 2) * Math.PI) / 180
	const width = (Math.max(...longitudes) - Math.min(...longitudes)) * METERS_PER_DEGREE_OF_LATITUDE * Math.cos(latitude)
	const height = (Math.max(...latitudes) - Math.min(...latitudes)) * METERS_PER_DEGREE_OF_LATITUDE
	return Math.max(width, height)
}

function frameView(
	map: mapboxgl.Map,
	view: ParcelMapView,
	footprint: [number, number][],
	center: [number, number]
): void {
	const camera = footprint.length
		? map.cameraForBounds(boundsAround(center, footprint), { padding: MAP_PADDING })
		: undefined
	map.jumpTo({ center, zoom: viewSpecs[view].zoom(camera?.zoom, center[1], parcelSpanInMeters(footprint)) })
}

function mapWidthInMeters(map: mapboxgl.Map): number {
	const latitude = (map.getCenter().lat * Math.PI) / 180
	const metersPerPixel = (EARTH_CIRCUMFERENCE_M * Math.cos(latitude)) / (512 * 2 ** map.getZoom())
	return MAP_WIDTH * metersPerPixel
}

function showView(map: mapboxgl.Map, container: HTMLElement, view: ParcelMapView, views: ParcelMapView[]): void {
	const spec = viewSpecs[view]
	container.style.height = `${spec.height}px`
	map.resize()
	for (const layer of new Set(views.flatMap(other => viewSpecs[other].layers))) {
		if (!map.getLayer(layer)) continue
		map.setLayoutProperty(layer, "visibility", spec.layers.includes(layer) ? "visible" : "none")
	}
}

export async function captureParcelMaps(
	options: ParcelMapSnapshotOptions,
	views: ParcelMapView[]
): Promise<ParcelMapCapture[]> {
	if (!Number.isFinite(options.latitude) || !Number.isFinite(options.longitude)) {
		throw new Error("Parcel coordinates are unavailable")
	}
	const captures: ParcelMapCapture[] = []
	for (const baseMap of new Set(views.map(view => viewSpecs[view].baseMap))) {
		const baseMapViews = views.filter(view => viewSpecs[view].baseMap === baseMap)
		captures.push(...(await captureBaseMapViews(options, baseMap, baseMapViews)))
	}
	return views.map(view => captures.find(capture => capture.view === view) as ParcelMapCapture)
}

async function captureBaseMapViews(
	options: ParcelMapSnapshotOptions,
	baseMap: MapStyleValue,
	views: ParcelMapView[]
): Promise<ParcelMapCapture[]> {
	const { parcelId, latitude, longitude, apiToken, outline, formatLength } = options
	const isSatellite = baseMap === MAP_STYLES.SATELLITE

	const container = document.createElement("div")
	Object.assign(container.style, {
		position: "fixed",
		left: `-${MAP_WIDTH + 100}px`,
		top: "0",
		width: `${MAP_WIDTH}px`,
		height: `${MAP_HEIGHT}px`,
		pointerEvents: "none",
	})
	document.body.appendChild(container)

	let map: mapboxgl.Map | undefined
	try {
		map = new mapboxgl.Map({
			container,
			accessToken: import.meta.env.VITE_MAPBOX_TOKEN,
			style: BASE_MAP_STYLES[baseMap],
			center: [longitude, latitude],
			zoom: DEFAULT_MAP_ZOOM,
			bearing: 0,
			pitch: 0,
			interactive: false,
			attributionControl: false,
			preserveDrawingBuffer: true,
			transformRequest: url => ({ url, headers: tileRequestHeaders({ url, apiToken }) }),
		})
		const snapshotMap = map

		let mapError: Error | undefined
		snapshotMap.on("error", (event: mapboxgl.ErrorEvent) => {
			mapError = new Error(event.error.message)
		})

		const waitFor = (event: "style.load" | "idle") =>
			new Promise<void>((resolve, reject) => {
				const timeout = window.setTimeout(
					() => reject(mapError ?? new Error("Map snapshot timed out")),
					CAPTURE_TIMEOUT_MS
				)
				snapshotMap.once(event, () => {
					window.clearTimeout(timeout)
					resolve()
				})
			})

		await waitFor("style.load")
		const baseUrl = import.meta.env.VITE_TILES_URL_DAILY
		addParcels(snapshotMap, {
			baseUrl,
			sourceName: "cadastre-source",
			layerName: "cadastre",
			visibleLayers: ["cadastre"],
			theme: isSatellite ? ParcelsTheme.Satellite : ParcelsTheme.Vector,
		})
		if (isSatellite)
			snapshotMap.setPaintProperty("cadastre-line", "line-opacity", SATELLITE_NEIGHBOUR_PARCEL_LINE_OPACITY)
		for (const view of views) addViewLayers(snapshotMap, view, baseUrl, options)
		if (views.some(view => HATCHED_VIEWS.includes(view))) await loadHatchPatterns(snapshotMap, () => true)
		addParcelHighlight(snapshotMap, parcelId, isSatellite)
		if (views.includes(ParcelMapView.CloseUp)) addParcelDimensions(snapshotMap, outline, formatLength)
		await waitFor("idle")
		let footprint = outline.length >= 3 ? outline : parcelFootprint(snapshotMap, parcelId)
		for (let attempt = 1; !footprint.length && attempt < FOOTPRINT_ATTEMPTS; attempt++) {
			const rendered = waitFor("idle")
			snapshotMap.triggerRepaint()
			await rendered
			footprint = parcelFootprint(snapshotMap, parcelId)
		}
		if (!footprint.length) console.warn(`Parcel ${parcelId} footprint not found, the maps stay centred on the parcel`)

		const captures: ParcelMapCapture[] = []
		for (const view of views) {
			showView(snapshotMap, container, view, views)
			frameView(snapshotMap, view, footprint, [longitude, latitude])
			const widthInMeters = mapWidthInMeters(snapshotMap)
			const rendered = waitFor("idle")
			snapshotMap.triggerRepaint()
			try {
				await rendered
				captures.push({ view, image: snapshotMap.getCanvas().toDataURL("image/jpeg", 0.9), widthInMeters })
			} catch (error) {
				console.error(`${view} map capture failed:`, error)
				captures.push({ view, image: null, widthInMeters })
			}
			options.onViewCaptured?.()
		}
		return captures
	} finally {
		map?.remove()
		container.remove()
	}
}
